import { cellToParent, getResolution } from "h3-js";

export interface WeatherView {
  cell: string; // the grid cell this reading covers
  gridRes: number;
  windFromDeg: number;
  speedKmh: number;
  gustKmh?: number;
}

/** Latest-wins fold over app.aid.weather.conditions, keyed by grid cell. Lookup is by
 *  containment: an incident reads the grid record whose cell contains its location. */
export class WeatherStore {
  #byCell = new Map<string, { seq: number; view: WeatherView }>();
  ingest(evt: { seq: number; json: Record<string, unknown> }): void {
    if (evt.json?.["$type"] !== "app.aid.weather.conditions") return;
    const p = (evt.json.payload ?? {}) as Record<string, unknown>;
    const cell = p.cell as string;
    const gridRes = p.gridRes as number | undefined;
    if (!cell || gridRes === undefined) return; // grid-cell records only (legacy ones skipped)
    const prev = this.#byCell.get(cell);
    if (prev && prev.seq > evt.seq) return;
    this.#byCell.set(cell, {
      seq: evt.seq,
      view: {
        cell,
        gridRes,
        windFromDeg: (p.windFromDeg as number) ?? 0,
        speedKmh: (p.speedKmh as number) ?? 0,
        gustKmh: p.gustKmh as number | undefined,
      },
    });
  }

  /** Wind for an incident at `incidentCell` (res-8): the grid record whose cell contains it.
   *  Several records at different grid resolutions can cover the same point (e.g. a manual ops
   *  reading at res-4 plus an auto reading at res-3, or an old reading from before the sampler
   *  changed grids). Return the FRESHEST (highest seq) so the wind never goes stale and the
   *  webapp label agrees with the projector's cone (which folds latest-wins too). */
  forCell(incidentCell: string): WeatherView | undefined {
    if (!incidentCell) return undefined;
    const r = getResolution(incidentCell);
    let best: { seq: number; view: WeatherView } | undefined;
    for (const entry of this.#byCell.values()) {
      const { view } = entry;
      if (view.gridRes <= r && cellToParent(incidentCell, view.gridRes) === view.cell) {
        if (!best || entry.seq > best.seq) best = entry;
      }
    }
    return best?.view;
  }
}
